import { apiFetch, hasLiveApi } from '@/api/client'

/** Lab `Member/searchHierarchyAggregate` request. */
export type SearchHierarchyAggregateRequest = {
  filter?: string | string[]
  page?: number
  accessLevel?: string
}

/** Lab Hierarchy Search row — upline columns by access level name. */
export type HierarchySearchRow = {
  _id: string
  username: string
  accessLevel: string
  Player?: string
  PlayerId?: string
  PlayerparentId?: string
  PlayerCreatedAt?: string
  Master?: string
  MasterId?: string
  MasterCreatedAt?: string
  SuperMaster?: string
  SuperMasterId?: string
  SuperMasterCreatedAt?: string
  DoubleMaster?: string
  DoubleMasterId?: string
  DoubleMasterCreatedAt?: string
  TripleMaster?: string
  TripleMasterId?: string
  TripleMasterCreatedAt?: string
  CompanyMaster?: string
  CompanyMasterId?: string
}

/** Standard API Response for Aggregate Hierarchy Search. */
export type SearchHierarchyAggregateResponse = {
  status: number
  message: string
  data: { data: HierarchySearchRow[]; count?: number; total?: number }
}

/** Lab Aggregate Hierarchy Search page size. */
export const HIERARCHY_PAGE_SIZE = 50

const LEVEL_KEYS = [
  'Player',
  'Master',
  'SuperMaster',
  'DoubleMaster',
  'TripleMaster',
  'CompanyMaster',
] as const

/** Normalize Lab Aggregate / chain row into HierarchySearchRow. */
export function normalizeHierarchySearchRow(
  raw: Record<string, unknown>,
): HierarchySearchRow | null {
  const id = String(raw._id ?? raw.id ?? '').trim()
  const username = String(raw.username ?? '').trim()
  if (!id && !username) return null

  const accessLevel = String(raw.accessLevel ?? '').trim() || inferAccessLevel(raw)
  const row: HierarchySearchRow = {
    _id: id || username,
    username: username || id,
    accessLevel,
  }

  for (const key of LEVEL_KEYS) {
    const name = raw[key]
    if (typeof name === 'string' && name.trim()) {
      row[key] = name.trim()
    }
    const idVal = raw[`${key}Id`]
    if (typeof idVal === 'string' && idVal.trim()) {
      ;(row as Record<string, string>)[`${key}Id`] = idVal.trim()
    }
    const created = raw[`${key}CreatedAt`]
    if (typeof created === 'string' && created.trim()) {
      ;(row as Record<string, string>)[`${key}CreatedAt`] = created.trim()
    }
  }

  if (typeof raw.PlayerparentId === 'string' && raw.PlayerparentId.trim()) {
    row.PlayerparentId = raw.PlayerparentId.trim()
  }

  if (!row.Player && row.username && (accessLevel === 'Player' || !accessLevel)) {
    row.Player = row.username
  }

  return row
}

function inferAccessLevel(raw: Record<string, unknown>): string {
  for (const key of LEVEL_KEYS) {
    if (typeof raw[key] === 'string' && raw[key]) return key
  }
  return 'Player'
}

export function normalizeHierarchySearchRows(list: unknown[]): HierarchySearchRow[] {
  const out: HierarchySearchRow[] = []
  for (const item of list) {
    if (!item || typeof item !== 'object') continue
    const row = normalizeHierarchySearchRow(item as Record<string, unknown>)
    if (row) out.push(row)
  }
  return out
}

/**
 * Lab `Member/searchHierarchyAggregate` — Aggregate group Hierarchy Search.
 * Scoped to the caller's memberList trees.
 */
export async function searchHierarchyAggregate(
  body: SearchHierarchyAggregateRequest,
): Promise<{ rows: HierarchySearchRow[]; total: number }> {
  const res = await apiFetch<SearchHierarchyAggregateResponse>(
    '/v1/member/searchHierarchyAggregate',
    {
      method: 'POST',
      body: JSON.stringify({
        ...body,
        accessLevel: body.accessLevel ?? 'Aggregate',
      }),
    },
  )
  if (res.status != null && res.status !== 200) return { rows: [], total: 0 }
  const rows = normalizeHierarchySearchRows(res.data?.data ?? [])
  return {
    rows,
    total: res.data?.count ?? res.data?.total ?? rows.length,
  }
}

/** Prefix autocomplete usernames from the same Aggregate endpoint. */
export async function suggestHierarchyAggregate(
  filter: string,
  page = 1,
): Promise<string[]> {
  const { rows } = await searchHierarchyAggregate({ filter, page })
  const names = rows.map((r) => r.username).filter(Boolean)
  return [...new Set(names)]
}

/**
 * Lab Aggregate Hierarchy Search columns (viewer treated as CompanyMaster).
 * Headers match Lab short labels used in the table.
 */
export function hierarchyColumnsForAggregate(): {
  id: 'Player' | 'Master' | 'SuperMaster' | 'DoubleMaster' | 'TripleMaster'
  header: string
  short: string
}[] {
  return [
    { id: 'Player', header: 'Player', short: 'P' },
    { id: 'Master', header: 'Master', short: 'M' },
    { id: 'SuperMaster', header: 'SM', short: 'SM' },
    { id: 'DoubleMaster', header: 'DM', short: 'DM' },
    { id: 'TripleMaster', header: 'TM', short: 'TM' },
  ]
}

/** Demo rows when hierarchy API is offline / demo session (Lab Aggregate shape). */
export const DEMO_HIERARCHY_ROWS: HierarchySearchRow[] = [
  {
    _id: '00000000-0000-4000-8000-000000000101',
    username: 'jaxoninr',
    accessLevel: 'Player',
    Player: 'jaxoninr',
    PlayerId: '00000000-0000-4000-8000-000000000101',
    Master: 'fingerprintinrmnew',
    MasterId: '00000000-0000-4000-8000-000000000051',
    SuperMaster: 'fingerprintinrsm',
    SuperMasterId: '00000000-0000-4000-8000-000000000031',
    DoubleMaster: 'fingerprintinrdm',
    DoubleMasterId: '00000000-0000-4000-8000-000000000021',
    TripleMaster: 'fingerprintinrtm',
    TripleMasterId: '00000000-0000-4000-8000-000000000011',
  },
  {
    _id: '00000000-0000-4000-8000-000000000102',
    username: 'mingpoint',
    accessLevel: 'Player',
    Player: 'mingpoint',
    PlayerId: '00000000-0000-4000-8000-000000000102',
    SuperMaster: 'fingerprintpointsm',
    SuperMasterId: '00000000-0000-4000-8000-000000000032',
    DoubleMaster: 'fingerprintpointdm',
    DoubleMasterId: '00000000-0000-4000-8000-000000000022',
    TripleMaster: 'fingerprintpointtm',
    TripleMasterId: '00000000-0000-4000-8000-000000000012',
  },
  {
    _id: '00000000-0000-4000-8000-000000000103',
    username: 'dieseltest',
    accessLevel: 'Player',
    Player: 'dieseltest',
    PlayerId: '00000000-0000-4000-8000-000000000103',
    TripleMaster: 'zpointzzaa33',
    TripleMasterId: '00000000-0000-4000-8000-000000000013',
  },
  // Agent hits — Lab exact-match returns the node itself, not every player under it.
  {
    _id: '00000000-0000-4000-8000-000000000051',
    username: 'fingerprintinrmnew',
    accessLevel: 'Master',
    Master: 'fingerprintinrmnew',
    MasterId: '00000000-0000-4000-8000-000000000051',
    SuperMaster: 'fingerprintinrsm',
    SuperMasterId: '00000000-0000-4000-8000-000000000031',
    DoubleMaster: 'fingerprintinrdm',
    DoubleMasterId: '00000000-0000-4000-8000-000000000021',
    TripleMaster: 'fingerprintinrtm',
    TripleMasterId: '00000000-0000-4000-8000-000000000011',
  },
  {
    _id: '00000000-0000-4000-8000-000000000032',
    username: 'fingerprintpointsm',
    accessLevel: 'SuperMaster',
    SuperMaster: 'fingerprintpointsm',
    SuperMasterId: '00000000-0000-4000-8000-000000000032',
    DoubleMaster: 'fingerprintpointdm',
    DoubleMasterId: '00000000-0000-4000-8000-000000000022',
    TripleMaster: 'fingerprintpointtm',
    TripleMasterId: '00000000-0000-4000-8000-000000000012',
  },
  {
    _id: '00000000-0000-4000-8000-000000000031',
    username: 'fingerprintinrsm',
    accessLevel: 'SuperMaster',
    SuperMaster: 'fingerprintinrsm',
    SuperMasterId: '00000000-0000-4000-8000-000000000031',
    DoubleMaster: 'fingerprintinrdm',
    DoubleMasterId: '00000000-0000-4000-8000-000000000021',
    TripleMaster: 'fingerprintinrtm',
    TripleMasterId: '00000000-0000-4000-8000-000000000011',
  },
  {
    _id: '00000000-0000-4000-8000-000000000022',
    username: 'fingerprintpointdm',
    accessLevel: 'DoubleMaster',
    DoubleMaster: 'fingerprintpointdm',
    DoubleMasterId: '00000000-0000-4000-8000-000000000022',
    TripleMaster: 'fingerprintpointtm',
    TripleMasterId: '00000000-0000-4000-8000-000000000012',
  },
  {
    _id: '00000000-0000-4000-8000-000000000021',
    username: 'fingerprintinrdm',
    accessLevel: 'DoubleMaster',
    DoubleMaster: 'fingerprintinrdm',
    DoubleMasterId: '00000000-0000-4000-8000-000000000021',
    TripleMaster: 'fingerprintinrtm',
    TripleMasterId: '00000000-0000-4000-8000-000000000011',
  },
  {
    _id: '00000000-0000-4000-8000-000000000012',
    username: 'fingerprintpointtm',
    accessLevel: 'TripleMaster',
    TripleMaster: 'fingerprintpointtm',
    TripleMasterId: '00000000-0000-4000-8000-000000000012',
  },
  {
    _id: '00000000-0000-4000-8000-000000000013',
    username: 'zpointzzaa33',
    accessLevel: 'TripleMaster',
    TripleMaster: 'zpointzzaa33',
    TripleMasterId: '00000000-0000-4000-8000-000000000013',
  },
]

export const DEMO_HIERARCHY_SUGGESTIONS = [
  'jaxoninr',
  'mingpoint',
  'dieseltest',
  'fingerprintinrmnew',
  'fingerprintpointsm',
  'fingerprintinrsm',
  'fingerprintpointdm',
  'fingerprintinrdm',
  'fingerprintinrtm',
  'fingerprintpointtm',
  'zpointzzaa33',
]

/**
 * Lab exact username match — only the hit node’s username, not upline columns.
 * (Matching DoubleMaster/TM columns reused player rows under that agent.)
 */
export function demoSearchHierarchyPage(
  names: string[],
): { rows: HierarchySearchRow[]; total: number } {
  const needles = new Set(names.map((n) => n.trim().toLowerCase()).filter(Boolean))
  if (!needles.size) return { rows: [], total: 0 }
  const rows = DEMO_HIERARCHY_ROWS.filter((row) =>
    needles.has(row.username.trim().toLowerCase()),
  )
  return { rows, total: rows.length }
}

export function demoSuggestUsernames(needle: string, exclude: string[] = []): string[] {
  const q = needle.trim().toLowerCase()
  const blocked = new Set(exclude.map((s) => s.toLowerCase()))
  return DEMO_HIERARCHY_SUGGESTIONS.filter((name) => {
    if (blocked.has(name.toLowerCase())) return false
    if (!q) return true
    return name.toLowerCase().includes(q)
  }).slice(0, 12)
}

/**
 * Autocomplete list for Hierarchy Search.
 * Live: API names only (empty API → empty list — never demo).
 * Demo: fixture usernames filtered by draft.
 */
export function hierarchySearchSuggestions(opts: {
  live: boolean
  apiNames?: string[] | null
  draft: string
  tags?: string[]
}): string[] {
  const tags = opts.tags ?? []
  const names = (
    opts.live ? (opts.apiNames ?? []) : demoSuggestUsernames(opts.draft, tags)
  ).filter((n) => !tags.includes(n))
  const needle = opts.draft.trim().toLowerCase()
  if (!needle || opts.live) return names.slice(0, 12)
  return names.filter((n) => n.toLowerCase().includes(needle)).slice(0, 12)
}

export function hierarchySearchLive(): boolean {
  return hasLiveApi()
}
